import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Loading from "../../components/core/blocks/Loading";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DevErrorDisplay } from "../../components/core/blocks/DevErrorDisplay";
import { formatISO } from "date-fns";
import { useState } from "react";
import { Modal } from "../../components/core/basic/Modal";
import { PeriodPicker } from "../../components/fields/base/Datepicker";
import { useCategoryGoalsQueryOptions } from "./api/useCategoryGoalsQueryOptions";
import { useSetCategoryGoals } from "./api/useSetCategoryGoals";
import { GoalsConfigForm, type Goal } from "./GoalsConfigForm";

export const CategoryGoalsConfigModal = NiceModal.create(({ categoryId }: { categoryId: number }) => {
  const { t } = useTranslation(["goals"]);
  const modal = useModal();
  const [displayYear, setDisplayYear] = useState(new Date());

  const categoryGoalQuery = useQuery({
    ...useCategoryGoalsQueryOptions(
      formatISO(new Date(displayYear.getFullYear(), 0, 1), {
        representation: "date",
      }),
      formatISO(new Date(displayYear.getFullYear(), 11, 31), {
        representation: "date",
      }),
    ),
    select(data) {
      // Transform the data into a record of goals per period
      return data.reduce(
        (acc, goal) => {
          if (goal.category_id !== categoryId) return acc;

          acc[goal.start_date] = {
            total_sales: goal.total_sales_goal,
            tacos: (goal.ad_spend_goal / goal.total_sales_goal) * 100,
            ad_spend: goal.ad_spend_goal,
          };
          return acc;
        },
        {} as Record<string, Goal>,
      );
    },
    refetchOnMount: true,
  });

  const setGoalsMutation = useSetCategoryGoals();

  return (
    <Modal
      isOpen={modal.visible}
      title={t(($) => $.goals["Configure Goals"])}
      onOpenChange={(isOpen) => !isOpen && modal.remove()}
    >
      <PeriodPicker setPeriod={setDisplayYear} period={displayYear} periodType={"year"} />
      {categoryGoalQuery.isLoading || !categoryGoalQuery.isFetchedAfterMount ?
        <Loading />
      : categoryGoalQuery.isSuccess ?
        <GoalsConfigForm
          initialValues={categoryGoalQuery.data}
          onSubmit={async (values) => {
            const payload = Object.entries(values)
              .filter(([, goal]) => !Number.isNaN(goal.total_sales) && !Number.isNaN(goal.ad_spend))
              .map(([start_date, goal]) => ({
                start_date,
                total_sales_goal: goal.total_sales,
                ad_spend_goal: goal.ad_spend,
                category_id: categoryId,
              }));
            await setGoalsMutation.mutateAsync(payload);
            modal.remove();
          }}
          onCancel={() => modal.remove()}
          displayYear={displayYear.getFullYear()}
        />
      : <DevErrorDisplay
          userMessage={t(($) => $.goals["There was an error loading the goal configuration"])}
          error={categoryGoalQuery.error}
        />
      }
    </Modal>
  );
});
